<template>
  <div v-if="stats" class="flex items-center gap-4 text-sm">
    <!-- Daily goal ring: the Settings goal finally gets a finish line. -->
    <div
      v-if="dailyGoal > 0"
      class="flex items-center gap-1"
      :title="`${xpToday} / ${dailyGoal} XP today`"
    >
      <svg class="w-5 h-5 -rotate-90" viewBox="0 0 20 20">
        <circle cx="10" cy="10" r="8" fill="none" stroke="rgba(255,255,255,0.25)" stroke-width="3" />
        <circle
          cx="10" cy="10" r="8" fill="none"
          stroke="#facc15" stroke-width="3" stroke-linecap="round"
          :stroke-dasharray="`${goalFraction * CIRCUMFERENCE} ${CIRCUMFERENCE}`"
        />
      </svg>
      <span class="text-xs font-semibold" :class="goalMet ? 'text-yellow-300' : 'text-emerald-200'">
        {{ goalMet ? 'Goal ✓' : `${xpToday}/${dailyGoal}` }}
      </span>
    </div>

    <!-- Streak freezes ride in this tooltip rather than becoming a fifth chip:
         the bar already carries four items at 375px alongside a back link, the
         dialect pill and two icons. A freeze is reassurance, not a metric. -->
    <div class="flex items-center gap-1" :title="streakTitle">
      <span class="text-orange-500">&#128293;</span>
      <span class="font-bold text-white">{{ stats.current_streak }}</span>
      <span v-if="freezes > 0" class="text-[10px] text-sky-200" aria-hidden="true">&#10052;{{ freezes }}</span>
    </div>

    <div class="flex items-center gap-1" :title="`Level ${stats.level}`">
      <span class="text-yellow-400">&#11088;</span>
      <span class="font-bold text-white">Lv. {{ stats.level }}</span>
    </div>

    <div class="flex items-center gap-2">
      <span class="text-emerald-200 text-xs">{{ stats.total_xp }} XP</span>
      <div class="w-16 bg-emerald-800 rounded-full h-1.5" :title="`${stats.xp_for_current_level} / ${stats.xp_for_next_level} XP to next level`">
        <div
          class="bg-yellow-400 h-1.5 rounded-full transition-all"
          :style="{ width: `${Math.min((stats.xp_for_current_level / stats.xp_for_next_level) * 100, 100)}%` }"
        ></div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet } from '../../api/client'

const stats = ref(null)

// The goal is stored on the ACCOUNT so it survives a change of device and the
// retention emails can reference it ("you're 20 XP from your goal"). The old
// localStorage key is still read as a fallback while accounts backfill, and as
// the pre-request value so the ring doesn't flash.
const GOAL_KEY = 'speaksaigon_daily_goal'
function localGoal() {
  try {
    const g = parseInt(localStorage.getItem(GOAL_KEY), 10)
    return Number.isNaN(g) ? 50 : g
  } catch (e) {
    return 50
  }
}
const dailyGoal = ref(localGoal())

const CIRCUMFERENCE = 2 * Math.PI * 8

const xpToday = computed(() => stats.value?.xp_today || 0)
const goalFraction = computed(() => Math.min(xpToday.value / dailyGoal.value, 1))
const goalMet = computed(() => xpToday.value >= dailyGoal.value)
const freezes = computed(() => stats.value?.streak_freezes || 0)
const streakTitle = computed(() => {
  const days = `${stats.value?.current_streak || 0} day streak`
  if (!freezes.value) return days
  const s = freezes.value === 1 ? '' : 's'
  return `${days} · ${freezes.value} streak freeze${s} saved — a missed day won't reset you`
})

onMounted(async () => {
  apiGet('/api/v1/account_prefs')
    .then(prefs => { if (prefs?.daily_goal) dailyGoal.value = prefs.daily_goal })
    .catch(() => {})

  try {
    stats.value = await apiGet('/api/v1/progress/stats')
  } catch (e) {
    // Stats are non-critical, fail silently
  }
})
</script>
